import { useState } from "react";
import type { EditionViewModel } from "../lib/adapter";
import { formatIST, formatTimeISTShort, formatDuration } from "../lib/datetime";

export function DemoBanner() {
  return (
    <aside className="demo-banner" aria-label="Demo data notice">
      <strong>Fixture demo · not live</strong>
      <span>All episode, run, source, verdict, audio, and schedule data below is frozen test data.</span>
    </aside>
  );
}

export function Masthead() {
  return (
    <header className="masthead" role="banner">
      <div className="masthead-brand">
        <span className="masthead-dot" aria-hidden="true" />
        <div className="masthead-wordmark">
          <span className="top">OVERNIGHT</span>
          <span className="bottom">NEWSROOM</span>
        </div>
      </div>
      <nav className="masthead-nav" aria-label="Primary">
        <a href="#latest">Latest</a>
        <a href="#rundown">Rundown</a>
        <a href="#timeline">How it works</a>
        <a href="#ledger">Sources</a>
      </nav>
    </header>
  );
}

export function Hero({ nextScheduled }: { nextScheduled: string | null }) {
  return (
    <section className="hero" aria-label="Product promise">
      <h1 className="hero-promise">
        The newsroom that never clocks out.
      </h1>
      <p className="hero-sub">
        A proof-first view of how Indian news feeds can become a fact-gated Hindi
        audio bulletin — written, checked, voiced, and published by agents.
      </p>
      <div className="hero-cta">
        <a href="#latest" className="btn btn-primary">
          Inspect the demo edition
        </a>
        <a
          href="https://t.me/anikul94new"
          className="btn btn-secondary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Join the Telegram channel
        </a>
        {nextScheduled && (
          <span className="hero-next">
            <span className="hero-next-dot" aria-hidden="true" />
            Demo schedule: {formatTimeISTShort(nextScheduled)} IST
          </span>
        )}
      </div>
    </section>
  );
}

export function EditionCard({ vm }: { vm: EditionViewModel }) {
  const statusClass = `status-${vm.page_state}`;
  const statusLabel = vm.page_state.replace(/_/g, " ").toUpperCase();

  return (
    <div className="edition-card" id="latest">
      {vm.correction_note && vm.corrected_at && (
        <div className="correction-banner" role="status">
          <div className="label">Correction — {formatIST(vm.corrected_at)}</div>
          <div className="note">{vm.correction_note}</div>
        </div>
      )}

      {vm.blocked_reason && (
        <div className="blocked-banner" role="alert">
          <div className="label">Edition withheld</div>
          <div className="reason">{vm.blocked_reason}</div>
        </div>
      )}

      <div className="edition-header">
        <h2 className="edition-title" lang="hi">{vm.title}</h2>
        <span className={`edition-status ${statusClass}`}>{statusLabel}</span>
      </div>

      <p className="hero-sub" style={{ marginTop: 0 }} lang="hi">{vm.caption}</p>

      {vm.audio_url && (
        <div className="edition-audio">
          <audio controls preload="none" aria-label="Demo edition audio">
            <source src={vm.audio_url} type="audio/mpeg" />
            Your browser does not support audio playback.
          </audio>
        </div>
      )}

      <div className="edition-meta">
        <div className="meta-item">
          <span className="meta-label">Published</span>
          <span className="meta-value">{formatIST(vm.published_at)}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Duration</span>
          <span className="meta-value">{formatDuration(vm.duration_seconds)}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Stories</span>
          <span className="meta-value">{vm.story_count}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Sources</span>
          <span className="meta-value">{vm.source_count}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Claims passed</span>
          <span className="meta-value">
            {vm.claim_counts.pass}/{vm.claim_counts.pass + vm.claim_counts.rewrite + vm.claim_counts.block}
          </span>
        </div>
      </div>

      {vm.audio_url && (
        <p className="fixture-footnote">Demo audio URL from the frozen publish fixture; availability is not asserted.</p>
      )}

      {vm.claim_counts.pass > 0 && (
        <div className="verification-badge">
          {vm.claim_counts.pass}/{vm.claim_counts.pass + vm.claim_counts.rewrite + vm.claim_counts.block} claims passed source gate
        </div>
      )}
    </div>
  );
}

export function Rundown({ vm }: { vm: EditionViewModel }) {
  if (vm.rundown.length === 0) return null;

  return (
    <section className="section" id="rundown">
      <div className="section-label">Today's Rundown</div>
      <ol className="rundown-list">
        {vm.rundown.map((item) => (
          <li key={item.rank} className="rundown-item">
            <span className="rundown-rank">{String(item.rank).padStart(2, "0")}</span>
            <span className="rundown-title">{item.story_title}</span>
            <span className="rundown-duration">{formatDuration(item.estimated_seconds)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function EditorialIntake({ vm }: { vm: EditionViewModel }) {
  return (
    <section className="section" aria-labelledby="intake-heading">
      <h2 className="section-label" id="intake-heading">Editorial intake</h2>
      <div className="intake-counts" aria-label="Editorial intake counts">
        <div><strong>{vm.selected_count}</strong><span>Accepted</span></div>
        <div><strong>{vm.excluded_count}</strong><span>Rejected</span></div>
        <p>{vm.candidate_count} fixture candidates evaluated against the frozen rundown.</p>
      </div>
    </section>
  );
}

export function Transcript({ vm }: { vm: EditionViewModel }) {
  if (vm.transcript.length === 0) return null;
  return (
    <section className="section" id="transcript" aria-labelledby="transcript-heading">
      <h2 className="section-label" id="transcript-heading">Hindi transcript</h2>
      <ol className="transcript-list" lang="hi">
        {vm.transcript.map((line) => (
          <li className={`transcript-line ${line.line_type}`} key={line.line_id}>
            <span className="transcript-speaker">
              {line.speaker.replace("ANCHOR_", "एंकर ")} · {line.line_id}
            </span>
            <p>{line.text}</p>
            {line.claim_ids.length > 0 && (
              <span className="transcript-claims">दावा {line.claim_ids.join(", ")}</span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function AgentTimeline({ vm }: { vm: EditionViewModel }) {
  return (
    <section className="section" id="timeline">
      <h2 className="section-label">Run timeline</h2>
      <div className="timeline" role="list">
        {vm.timeline.map((stage) => (
          <div key={stage.name} className="timeline-stage" role="listitem">
            <span className="timeline-time">
              {stage.timestamp ? formatTimeISTShort(stage.timestamp) : "—"}
            </span>
            <div className="timeline-body">
              <div className="timeline-name">{stage.name}</div>
              <div className="timeline-detail">{stage.detail}</div>
            </div>
            <div className="timeline-meta">
              {stage.duration_seconds > 0 && (
                <span className="timeline-duration">
                  {stage.duration_seconds.toFixed(1)}s
                </span>
              )}
              <span className={`timeline-icon ${stage.status}`} aria-label={stage.status}>
                {stage.status === "done" ? "✓" : stage.status === "blocked" ? "✕" : "—"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ClaimLedger({ vm }: { vm: EditionViewModel }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (vm.ledger.length === 0) return null;

  return (
    <section className="section" id="ledger">
      <h2 className="section-label">Source ledger &amp; claim verdicts</h2>
      <div className="ledger">
        {vm.ledger.map((entry) => {
          const id = `${entry.line_id}-${entry.claim_id}`;
          const isOpen = expanded.has(id);
          return (
            <div key={id} className="ledger-entry" data-expanded={isOpen}>
              <button
                className="ledger-summary"
                onClick={() => toggle(id)}
                aria-expanded={isOpen}
                aria-controls={`ledger-detail-${id}`}
              >
                <div className="ledger-summary-left">
                  <span className="ledger-line-id">{entry.line_id}</span>
                  <span className="ledger-speaker">{entry.speaker.replace("ANCHOR_", "Anchor ")}</span>
                  <span className="ledger-text" lang="hi">{entry.text}</span>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <span className={`ledger-verdict ${entry.verdict.toLowerCase()}`}>
                    {entry.verdict}
                  </span>
                  <span className="ledger-chevron" aria-hidden="true">▶</span>
                </div>
              </button>
              <div className="ledger-detail" id={`ledger-detail-${id}`}>
                <div className="ledger-hindi" lang="hi">{entry.text}</div>
                <div className="ledger-claim-id">Claim {entry.claim_id}</div>
                <div className="ledger-evidence">
                  <div className="ledger-evidence-label">Evidence excerpt</div>
                  <div className="ledger-excerpt">"{entry.excerpt}"</div>
                </div>
                <div className="ledger-source">
                  <span className="ledger-evidence-label">Source</span>
                  <br />
                  <a href={entry.source_url} target="_blank" rel="noopener noreferrer">
                    {entry.source_id} ↗
                  </a>
                </div>
                <div className="ledger-retrieved">
                  Retrieved: {formatIST(entry.retrieved_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function BlockedExample({ vm }: { vm: EditionViewModel }) {
  if (vm.blocked_examples.length === 0) return null;

  return (
    <section className="section">
      <div className="section-label">Blocked Before Air</div>
      {vm.blocked_examples.map((ex) => (
        <div key={ex.line_id} className="blocked-example">
          <div className="label">Blocked Before Air</div>
          <div className="line-id">Line {ex.line_id}</div>
          <div className="reason">{ex.reason}</div>
          <div className="action">Action: {ex.action}</div>
        </div>
      ))}
    </section>
  );
}

export function TelegramCTA({ vm }: { vm: EditionViewModel }) {
  const url = vm.telegram_url ?? "https://t.me/anikul94new";

  return (
    <section className="section">
      <div className="telegram-cta">
        <h3>Get the next verified bulletin</h3>
        <p>Hindi audio at scheduled points through the day.</p>
        <a
          href={url}
          className="btn btn-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Join on Telegram
        </a>
      </div>
    </section>
  );
}

export function PreviousEditions({ vm }: { vm: EditionViewModel }) {
  if (vm.previous_editions.length === 0) return null;

  return (
    <section className="section">
      <div className="section-label">Previous Editions</div>
      <div className="previous-grid">
        {vm.previous_editions.map((ed) => (
          <div key={ed.episode_slug} className="previous-card">
            <div className="title" lang="hi">{ed.title}</div>
            <div className="meta">
              {formatIST(ed.published_at)} · {formatDuration(ed.duration_seconds)}
            </div>
            <div className="claims">
              {ed.story_count} stories · {ed.source_count} sources · {ed.claim_counts.pass}/{ed.claim_counts.pass + ed.claim_counts.rewrite + ed.claim_counts.block} claims passed
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Disclosure() {
  return (
    <section className="section">
      <div className="section-label">AI-Generated &amp; Source-Gated</div>
      <div className="disclosure">
        <p>
          <strong>AI-generated voices and script.</strong> Every spoken factual
          line is source-gated against linked reporting. The system guarantees
          traceability and policy compliance against its source pack; it does not
          guarantee that the sources themselves are infallible. Read the original
          reporting for full context.
        </p>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <p>
        Overnight Newsroom · Autonomous Hindi audio bulletin · Built with Hermes Agent
      </p>
    </footer>
  );
}

export function LoadingState() {
  return (
    <div className="edition-card">
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-line" style={{ width: "80%" }} />
      <div className="skeleton skeleton-line" style={{ width: "60%" }} />
      <div className="skeleton skeleton-line" style={{ width: "40%", height: "3rem", marginTop: "1rem" }} />
    </div>
  );
}

export function NoEditionState({ nextScheduled }: { nextScheduled: string | null }) {
  return (
    <div className="edition-card no-edition" id="latest">
      <p>
        No verified edition was produced from this fixture run.
        {nextScheduled && (
          <>
            <br />
            Next scheduled run: {formatTimeISTShort(nextScheduled)} IST.
          </>
        )}
      </p>
    </div>
  );
}
